import type { QuestionLibraryCatalogCourse } from 'services/api/adminContentClient';

export const assignmentCount = (course: QuestionLibraryCatalogCourse) =>
  (course.learner_count ?? 0) + (course.organization_count ?? 0) + (course.cohort_count ?? 0);

/**
 * How the Course Builder list and the Module Builder course picker group courses:
 * - assigned: courses assigned to anyone (learners, organizations or cohorts), even if the name looks like a test,
 *   because someone is using them. Life Plan first, then by learner count.
 * - other: not assigned and not test-like, by name.
 * - tests: not assigned and test-like (name heuristic or QA lifecycle), newest first; shown folded.
 */
export const groupCoursesForBuilders = (courses: QuestionLibraryCatalogCourse[]) => {
  const byName = (a: QuestionLibraryCatalogCourse, b: QuestionLibraryCatalogCourse) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) || a.id - b.id;
  const assigned = courses
    .filter((course) => assignmentCount(course) > 0)
    .sort((a, b) =>
      Number(b.is_life_plan) - Number(a.is_life_plan)
      || (b.learner_count ?? 0) - (a.learner_count ?? 0)
      || assignmentCount(b) - assignmentCount(a)
      || byName(a, b));
  const unassigned = courses.filter((course) => assignmentCount(course) === 0);
  const other = unassigned
    .filter((course) => !course.looks_like_test)
    .sort((a, b) => Number(b.is_life_plan) - Number(a.is_life_plan) || byName(a, b));
  const tests = unassigned
    .filter((course) => course.looks_like_test)
    .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '') || byName(a, b));
  return { assigned, other, tests };
};

/** "20 modules · 3 chapters · 20 learners · 1 org" */
export const courseMetaLine = (course: QuestionLibraryCatalogCourse) => {
  const parts = [`${course.module_count} module${course.module_count === 1 ? '' : 's'}`];
  if (course.chapter_count) parts.push(`${course.chapter_count} chapter${course.chapter_count === 1 ? '' : 's'}`);
  if (course.learner_count) parts.push(`${course.learner_count} learner${course.learner_count === 1 ? '' : 's'}`);
  if (course.organization_count) parts.push(`${course.organization_count} org${course.organization_count === 1 ? '' : 's'}`);
  if (course.cohort_count) parts.push(`${course.cohort_count} cohort${course.cohort_count === 1 ? '' : 's'}`);
  return parts.join(' · ');
};
